import React from 'react';
import { captureDashboardRouteLoadingIssue } from '../services/sentryEvents';

type ComponentModule<TProps extends object = Record<string, never>> = {
    default?: React.ComponentType<TProps>;
};

const isComponent = (value: unknown): value is React.ComponentType<any> => {
    return typeof value === 'function' || (typeof value === 'object' && value !== null);
};

const LAZY_ROUTE_TIMEOUT_MS = 15000;

const withRouteLoadTimeout = <T,>(promise: Promise<T>, label: string): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            captureDashboardRouteLoadingIssue('lazy_route_timeout', {
                routeLabel: label,
                timeoutMs: LAZY_ROUTE_TIMEOUT_MS,
            });
            reject(new Error(`Lazy route "${label}" timed out while loading`));
        }, LAZY_ROUTE_TIMEOUT_MS);
    });

    return Promise.race([promise, timeout]).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
    });
};

export const lazyComponent = <TProps extends object>(
    loader: () => Promise<ComponentModule<TProps>>,
    label: string,
) => React.lazy(async () => {
    const module = await withRouteLoadTimeout(loader(), label);
    if (!isComponent(module.default)) {
        throw new Error(`Lazy route "${label}" did not resolve to a default React component`);
    }
    return { default: module.default };
});
